import React from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Target, ChevronDown, Trash2, Plus, RefreshCw } from "lucide-react";

interface ScalingSettingsProps {
  form: any;
  setForm: React.Dispatch<React.SetStateAction<any>>;
  row: (label: string, inputEl: React.ReactNode, helper?: string) => React.ReactNode;
  field: (key: string) => any;
  isReadOnly: boolean;
  scalingMode: string;
  setScalingMode: (mode: string) => void;
  showCustomTargets: boolean;
  setShowCustomTargets: (show: boolean) => void;
  bot: any;
  currency: string;
  syncAutoToCustom: () => void;
  handleEntryTypeChange: (type: string) => void;
}

export const ScalingSettings: React.FC<ScalingSettingsProps> = ({
  form, setForm, row, field, isReadOnly, scalingMode, setScalingMode, showCustomTargets, setShowCustomTargets, bot, currency, syncAutoToCustom, handleEntryTypeChange
}) => {
  return (
    <div className={`pt-6 border-t border-border space-y-6 ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-black text-foreground uppercase tracking-widest flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5" /> 물타기(Scaling) 및 익절 전략
        </h3>
        <div className="flex bg-muted rounded-lg p-0.5 h-8 w-44">
          <Button disabled={isReadOnly} variant={scalingMode === 'auto' ? "default" : "ghost"} size="sm" onClick={() => setScalingMode('auto')} className={`flex-1 h-full text-[10px] font-black rounded-md ${scalingMode === 'auto' ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground'}`}>자동 계산</Button>
          <Button disabled={isReadOnly} variant={scalingMode === 'custom' ? "default" : "ghost"} size="sm" onClick={() => setScalingMode('custom')} className={`flex-1 h-full text-[10px] font-black rounded-md ${scalingMode === 'custom' ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground'}`}>직접 입력</Button>
        </div>
      </div>

      {/* Compact Grid Section for TP, Limit TP, Offsetting, Entry Mode */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Card 1: 목표 익절 (ROE %) */}
        <div className="p-4 bg-primary/5 rounded-2xl border border-primary/10 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-[11px] font-black text-foreground uppercase">목표 익절 (ROE %)</h4>
            <Badge variant="outline" className="text-[9px] font-black border-primary/30 text-primary">{form.tp_pct}%</Badge>
          </div>
          <div className="relative">
            <input disabled={isReadOnly} type="number" step="0.1" min="0.1" className="w-full h-10 rounded-xl border border-border pl-3 pr-8 text-sm font-black bg-background" {...field('tp_pct')} />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-success">%</span>
          </div>

          {/* [NEW] 분할 익절 설정 */}
          <div className="pt-2 border-t border-primary/5 space-y-2">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setForm((f: any) => ({ ...f, use_partial_tp: !f.use_partial_tp }))}>
              <label className="text-[11px] font-black text-foreground uppercase">분할 익절 사용</label>
              <input type="checkbox" className="w-4 h-4 rounded text-primary cursor-pointer" checked={form.use_partial_tp || false} readOnly />
            </div>
            {form.use_partial_tp && (
              <div className="grid grid-cols-2 gap-2 animate-in fade-in slide-in-from-top-1">
                <div className="space-y-1">
                  <label className="text-[8px] font-black text-muted-foreground uppercase leading-none">1차 TP 비율 (%)</label>
                  <div className="relative mt-1">
                    <input disabled={isReadOnly} type="number" step="0.1" className="w-full h-8 rounded-lg border border-border pl-2 pr-6 text-[10px] font-bold bg-background" value={form.partial_tp_rate1 ?? 66.66} onChange={(e) => setForm({ ...form, partial_tp_rate1: Number(e.target.value) })} />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[8px] font-bold text-muted-foreground">%</span>
                  </div>
                  <span className="text-[7px] text-muted-foreground font-semibold leading-none">최종 TP 대비 비율</span>
                </div>
                <div className="space-y-1">
                  <label className="text-[8px] font-black text-muted-foreground uppercase leading-none">1차 청산 수량 (%)</label>
                  <div className="relative mt-1">
                    <input disabled={isReadOnly} type="number" step="1" className="w-full h-8 rounded-lg border border-border pl-2 pr-6 text-[10px] font-bold bg-background" value={form.partial_tp_qty_rate1 ?? 50} onChange={(e) => setForm({ ...form, partial_tp_qty_rate1: Number(e.target.value) })} />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[8px] font-bold text-muted-foreground">%</span>
                  </div>
                  <span className="text-[7px] text-muted-foreground font-semibold leading-none">보유 물량 대비 비율</span>
                </div>
                <div className="col-span-2 flex items-center justify-between cursor-pointer pt-1" onClick={() => setForm((f: any) => ({ ...f, use_breakeven_after_partial: !f.use_breakeven_after_partial }))}>
                  <label className="text-[9px] font-black text-primary uppercase cursor-pointer">1차 익절 후 본절 청산</label>
                  <input type="checkbox" className="w-3.5 h-3.5 rounded text-primary cursor-pointer" checked={form.use_breakeven_after_partial ?? true} readOnly />
                </div>
                <div className="col-span-2 pt-1 border-t border-primary/5 space-y-1">
                  <label className="text-[8px] font-black text-muted-foreground uppercase">분할익절 최소 작동 타겟(%)</label>
                  <div className="relative">
                    <input disabled={isReadOnly} type="number" step="0.1" className="w-full h-8 rounded-lg border border-border pl-2 pr-6 text-[10px] font-bold bg-background" value={form.min_partial_tp_pct ?? 1.0} onChange={(e) => setForm({ ...form, min_partial_tp_pct: Number(e.target.value) })} />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[8px] font-bold text-muted-foreground">%</span>
                  </div>
                  <span className="text-[7px] text-muted-foreground font-semibold leading-none">목표 익절%가 이 값 이하일 때 분할익절을 생략하고 일괄 청산합니다.</span>
                </div>
              </div>
            )}
          </div>

          <div className="pt-2 border-t border-primary/5 space-y-3">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => {
              const nextVal = !form.use_dynamic_tp;
              setForm((f: any) => ({
                ...f,
                use_dynamic_tp: nextVal,
                tp_threshold_step: nextVal ? (f.tp_threshold_step && Number(f.tp_threshold_step) > 0 ? f.tp_threshold_step : 2) : 0,
                tp_threshold_pct: nextVal ? (f.tp_threshold_pct && Number(f.tp_threshold_pct) > 0 ? f.tp_threshold_pct : 1.5) : 0
              }));
            }}>
              <h4 className="text-[11px] font-black text-foreground uppercase">동적 익절 사용</h4>
              <input type="checkbox" className="w-4 h-4 rounded text-primary cursor-pointer" checked={form.use_dynamic_tp || false} readOnly />
            </div>

            {form.use_dynamic_tp && (
              <div className="grid grid-cols-2 gap-2 animate-in fade-in slide-in-from-top-1 duration-200">
                <div className="space-y-1">
                  <label className="text-[8px] font-black text-muted-foreground uppercase">시작 차수</label>
                  <div className="relative">
                    <input type="number" className="w-full h-7 rounded-lg border border-border pl-2 pr-8 text-[10px] font-bold bg-background" {...field('tp_threshold_step')} />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[8px] font-black text-muted-foreground">차 이후</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[8px] font-black text-muted-foreground uppercase">동적목표(%)</label>
                  <div className="relative">
                    <input type="number" step="0.1" className="w-full h-7 rounded-lg border border-border pl-2 pr-5 text-[10px] font-bold bg-background" {...field('tp_threshold_pct')} />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[8px] font-black text-muted-foreground">%</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 수수료 보정 익절가 옵션 */}
          <div className="pt-2 border-t border-primary/5 flex items-center justify-between cursor-pointer" onClick={() => setForm((f: any) => ({ ...f, use_net_profit_tp: !f.use_net_profit_tp }))}>
            <div className="space-y-0.5">
              <h4 className="text-[11px] font-black text-foreground uppercase">수수료 보정 익절가</h4>
              <p className="text-[8px] text-muted-foreground font-bold leading-none">수수료만큼 목표가 자동 가산</p>
            </div>
            <input type="checkbox" className="w-4 h-4 rounded text-primary cursor-pointer animate-none" checked={form.use_net_profit_tp ?? true} readOnly />
          </div>
        </div>

        {/* Card 2: 지정가 익절 (Limit TP) */}
        <div className="p-4 bg-muted/30 rounded-2xl border border-border space-y-3 relative">
          <div className="flex items-center justify-between cursor-pointer" onClick={() => {
            if (form.use_partial_tp) return;
            setForm((f: any) => ({ ...f, use_limit_tp: !f.use_limit_tp }));
          }}>
            <div className="space-y-0.5">
              <h4 className={`text-[11px] font-black uppercase ${form.use_partial_tp ? 'text-muted-foreground line-through' : 'text-foreground'}`}>지정가 익절 사용</h4>
              {form.use_partial_tp && (
                <p className="text-[8px] text-rose-500 font-extrabold animate-pulse">⚠️ 분할 익절 활성화 시 지정가 익절 사용 불가</p>
              )}
            </div>
            <input type="checkbox" className="w-4 h-4 rounded text-primary cursor-pointer disabled:opacity-40" checked={!form.use_partial_tp && form.use_limit_tp} disabled={form.use_partial_tp} readOnly />
          </div>
          {!form.use_partial_tp && form.use_limit_tp && (
            <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
              <div className="flex justify-between items-center">
                <label className="text-[9px] font-black text-muted-foreground uppercase">되돌림 방어 (Pullback %)</label>
                <Badge className="bg-primary text-white text-[8px] h-4">{form.pullback_pct}%</Badge>
              </div>
              <div className="relative">
                <input type="number" step="0.01" className="w-full h-10 rounded-xl border border-border pl-3 pr-8 text-sm font-black bg-background" {...field('pullback_pct')} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-primary">%</span>
              </div>
            </div>
          )}
        </div>

        {/* Card 3: 수익 상계 처리 (Offsetting) */}
        {bot.side === 'both' && (
          <div className="p-4 bg-muted/30 rounded-2xl border border-border space-y-3">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setForm((f: any) => ({ ...f, use_profit_offsetting: !f.use_profit_offsetting }))}>
              <h4 className="text-[11px] font-black text-foreground uppercase">수익 상계 처리</h4>
              <input type="checkbox" className="w-4 h-4 rounded text-primary" checked={form.use_profit_offsetting} readOnly />
            </div>
            {form.use_profit_offsetting && (
              <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
                <div className="flex justify-between items-center">
                  <label className="text-[9px] font-black text-muted-foreground uppercase">상계 비율 (Offset %)</label>
                  <Badge variant="outline" className="text-[8px] h-4 border-primary/30 text-primary">{form.profit_offset_pct}%</Badge>
                </div>
                <div className="relative">
                  <input type="number" step="1" className="w-full h-10 rounded-xl border border-border pl-3 pr-8 text-sm font-black bg-background" {...field('profit_offset_pct')} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-primary">%</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Card 4: 진입 규모 타입 */}
        <div className="p-4 bg-muted/30 rounded-2xl border border-border space-y-3">
          <h4 className="text-[11px] font-black text-foreground uppercase">진입 규모 타입</h4>
          <div className="flex bg-background/50 p-1 rounded-lg gap-1 h-10">
            <Button variant={form.entry_type === 'fixed' ? "default" : "ghost"} size="sm" onClick={() => handleEntryTypeChange('fixed')} className={`flex-1 h-full text-[10px] font-bold ${form.entry_type === 'fixed' ? 'bg-primary text-white shadow-sm' : ''}`}>고정 금액 ($)</Button>
            <Button variant={form.entry_type === 'percent' ? "default" : "ghost"} size="sm" onClick={() => handleEntryTypeChange('percent')} className={`flex-1 h-full text-[10px] font-bold ${form.entry_type === 'percent' ? 'bg-primary text-white shadow-sm' : ''}`}>잔고 비율 (%)</Button>
          </div>
        </div>
      </div>

      {/* Scaling Parameters Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 pt-6 border-t border-border/50">
        {row(`첫 진입 ${form.entry_type === 'percent' ? '비율' : '비용'}`, (
          <div className="w-full relative">
            <input type="number" className="w-full h-10 rounded-xl border border-border pl-3 pr-10 text-sm font-black bg-background" {...field('entry_amount')} />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">{form.entry_type === 'percent' ? '%' : currency}</span>
          </div>
        ))}

        {row('최대 물타기 횟수', <input disabled={isReadOnly} type="number" className="w-full h-10 rounded-xl border border-border px-3 text-sm font-black bg-background disabled:bg-muted/50" {...field('max_scaling')} />)}

        {scalingMode === 'auto' && (
          <>
            {row('진입 간격 (%)', <input disabled={isReadOnly} type="number" step="0.1" className="w-full h-10 rounded-xl border border-border px-3 text-sm font-black bg-background disabled:bg-muted/50" {...field('step_pct')} />)}
            {row('수량 배수', <input disabled={isReadOnly} type="number" step="0.1" className="w-full h-10 rounded-xl border border-border px-3 text-sm font-black bg-background disabled:bg-muted/50" {...field('multiplier')} />)}
            {row('이격도 기반 간격 조절 (Disparity Gap)', (
              <div className="flex items-center gap-3">
                <input type="checkbox" className="w-4 h-4 rounded-lg border-border text-primary" checked={form.use_disparity_gap} onChange={(e) => setForm((f: any) => ({ ...f, use_disparity_gap: e.target.checked }))} />
                <div className="flex-1 relative">
                  <input type="number" step="0.1" className="w-full h-10 rounded-xl border border-border px-3 font-black text-sm bg-background disabled:opacity-30" disabled={!form.use_disparity_gap} value={form.disparity_factor} onChange={(e) => setForm((f: any) => ({ ...f, disparity_factor: Number(e.target.value) }))} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-muted-foreground uppercase">% 계수</span>
                </div>
              </div>
            ), '차수가 깊어질수록 간격(%)을 계수만큼 추가로 넓혀 대응합니다.')}
          </>
        )}
      </div>

      <div className="col-span-full mt-2">
        {scalingMode === 'auto' ? (
          <div className="space-y-4">
            <div className="bg-white rounded-[24px] border border-slate-200 overflow-hidden shadow-sm">
              <div className="flex justify-between items-center cursor-pointer p-4 hover:bg-slate-50 transition-all group" onClick={() => setShowCustomTargets(!showCustomTargets)}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center">
                    <Target className="w-4 h-4 text-slate-400" />
                  </div>
                  <span className="text-[14px] font-black text-slate-800">자동 생성 사다리 미리보기</span>
                  <Badge variant="secondary" className="bg-slate-100 text-slate-600 border-none px-2.5 py-0.5 text-[11px] font-bold">{form.max_scaling} 차수</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-tighter mr-1">{showCustomTargets ? '접기' : '보기'}</span>
                  <ChevronDown className={`w-4 h-4 text-slate-300 transition-transform duration-300 ${showCustomTargets ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {showCustomTargets && (
                <div className="border-t border-slate-100 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase border-b border-slate-100">
                          <th className="px-4 py-3 w-16">STEP</th>
                          <th className="px-4 py-3 text-center">증거금($)</th>
                          <th className="px-4 py-3 text-center">평단</th>
                          <th className="px-4 py-3 text-center">간격(%)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(() => {
                          let cumulativeDist = 0;
                          let cumulativeCost = 0;
                          let cumulativeQty = 0;
                          return Array.from({ length: Number(form.max_scaling) || 0 }).map((_, i) => {
                            const step = i + 1;
                            const isPercent = form.entry_type === 'percent';
                            let balanceBasis = Number(bot.principal_amount || form.balance || bot.total_equity || 0);
                            if (balanceBasis <= 0) balanceBasis = 1000;
                            const baseAmount = isPercent ? (balanceBasis * (Number(form.entry_amount) / 100)) : (Number(form.entry_amount) || 0);
                            const cost = baseAmount * Math.pow(Number(form.multiplier), i);
                            const dist = i === 0 ? 0 : (form.use_disparity_gap ? Number(form.step_pct) * (1 + (Math.max(0, i - 1) * Number(form.disparity_factor) / 100)) : Number(form.step_pct)) || 0;
                            cumulativeDist += dist;

                            // 가중 평균 평단가 정밀 계산 (기준가 1 기준)
                            const currentPrice = 1 - (cumulativeDist / 100);
                            const qty = cost / (currentPrice > 0 ? currentPrice : 1);
                            cumulativeCost += cost;
                            cumulativeQty += qty;
                            const avgPrice = cumulativeCost / (cumulativeQty > 0 ? cumulativeQty : 1);
                            const avgDistPct = (1 - avgPrice) * 100;

                            return (
                              <tr key={i} className="hover:bg-slate-50/30 transition-colors h-11">
                                <td className="px-4 py-0 font-black text-slate-400 text-[12px]">#{step}</td>
                                <td className="px-4 py-0 text-center font-black text-slate-700 text-[13px]">{cost.toFixed(2)}</td>
                                <td className="px-4 py-0 text-center">
                                  <span className="text-[11px] font-bold text-amber-600">{i === 0 ? '-' : `-${avgDistPct.toFixed(2)}%`}</span>
                                </td>
                                <td className="px-4 py-0 text-center font-bold text-slate-700 text-[12px]">{cumulativeDist.toFixed(2)}%</td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>

                  {/* Footer with Badge style */}
                  <div className="bg-slate-50/80 p-4 border-t border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-200/50 rounded-full">
                      <span className="text-[10px] font-black text-slate-500 uppercase">TOTAL COST</span>
                      <span className="text-[12px] font-black text-slate-700">{(form.max_scaling > 0 ? Array.from({ length: Number(form.max_scaling) }).reduce((acc: number, _, i) => acc + ((form.entry_type === 'percent' ? (Number(bot.principal_amount || form.balance || 1000) * Number(form.entry_amount) / 100) : Number(form.entry_amount)) * Math.pow(Number(form.multiplier), i)), 0) : 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-200/50 rounded-full">
                      <span className="text-[10px] font-black text-slate-500 uppercase">NOTIONAL</span>
                      <span className="text-[12px] font-black text-slate-700">{((form.max_scaling > 0 ? Array.from({ length: Number(form.max_scaling) }).reduce((acc: number, _, i) => acc + ((form.entry_type === 'percent' ? (Number(bot.principal_amount || form.balance || 1000) * Number(form.entry_amount) / 100) : Number(form.entry_amount)) * Math.pow(Number(form.multiplier), i)), 0) : 0) * (form.leverage || 1)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-white rounded-[24px] border border-slate-200 overflow-hidden shadow-sm">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center">
                    <Target className="w-4 h-4 text-indigo-600" />
                  </div>
                  <h3 className="text-[14px] font-black text-slate-800">커스텀 분할 진입 사다리 설정</h3>
                  <Badge variant="secondary" className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 border-none px-2.5 py-0.5 text-[11px] font-bold">{form.custom_targets?.length || 0} 차수</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={syncAutoToCustom} className="h-9 px-4 rounded-xl border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-[12px] font-bold gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                    자동 계산값 불러오기
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => {
                    const newTargets = [...(form.custom_targets || [])];
                    const lastStep = newTargets[newTargets.length - 1];
                    newTargets.push({
                      step_number: (lastStep?.step_number || 0) + 1,
                      target_price_pct: (lastStep?.target_price_pct || 0) + 1.0,
                      target_qty: (lastStep?.target_qty || 10),
                      target_price: 0
                    });
                    // [Sync] 회차 추가 시 최대 물타기 횟수도 함께 업데이트
                    setForm({ ...form, custom_targets: newTargets, max_scaling: newTargets.length });
                  }} className="h-9 px-4 rounded-xl border-slate-200 bg-white text-slate-600 hover:bg-slate-50 text-[12px] font-bold gap-2">
                    <Plus className="w-4 h-4" /> 회차 추가
                  </Button>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase border-b border-slate-100">
                      <th className="px-4 py-3 w-16">STEP</th>
                      <th className="px-4 py-3 text-center">증거금($)</th>
                      <th className="px-4 py-3 text-center">평단</th>
                      <th className="px-4 py-3 text-center">간격(%)</th>
                      <th className="px-4 py-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(() => {
                      let cumulativeCost = 0;
                      let cumulativeQty = 0;
                      return form.custom_targets?.map((target: any, index: number) => {
                        const cost = Number(target.target_qty) || 0;
                        const dist = Number(target.target_price_pct) || 0;

                        // 가중 평균 평단가 정밀 계산 (기준가 1 기준)
                        const currentPrice = 1 - (dist / 100);
                        const qty = cost / (currentPrice > 0 ? currentPrice : 1);
                        cumulativeCost += cost;
                        cumulativeQty += qty;
                        const avgPrice = cumulativeCost / (cumulativeQty > 0 ? cumulativeQty : 1);
                        const avgDistPct = (1 - avgPrice) * 100;

                        return (
                          <tr key={index} className="hover:bg-slate-50/30 transition-colors">
                            <td className="px-4 py-2 font-black text-slate-400 text-[12px]">#{target.step_number}</td>
                            <td className="px-4 py-2">
                              <input type="number" className="w-full h-10 rounded-xl border border-slate-200 bg-white px-3 text-center font-black text-slate-700 text-[13px] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all" value={target.target_qty || ''} onChange={(e) => {
                                const newTargets = [...form.custom_targets];
                                newTargets[index].target_qty = Number(e.target.value);
                                setForm({ ...form, custom_targets: newTargets });
                              }} />
                            </td>
                            <td className="px-4 py-2 text-center">
                              <span className="text-[11px] font-bold text-amber-600">{index === 0 ? '-' : `-${avgDistPct.toFixed(2)}%`}</span>
                            </td>
                            <td className="px-4 py-2">
                              <input type="number" step="0.1" className="w-full h-10 rounded-xl border border-slate-200 bg-white px-3 text-center font-black text-slate-700 text-[13px] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all" value={target.target_price_pct || ''} onChange={(e) => {
                                const newTargets = [...form.custom_targets];
                                newTargets[index].target_price_pct = Number(e.target.value);
                                setForm({ ...form, custom_targets: newTargets });
                              }} />
                            </td>
                            <td className="px-4 py-2">
                              <button onClick={() => {
                                const newTargets = form.custom_targets.filter((_: any, i: number) => i !== index);
                                // [Sync] 회차 삭제 시 최대 물타기 횟수도 함께 업데이트
                                setForm({ ...form, custom_targets: newTargets, max_scaling: newTargets.length });
                              }} className="p-2 text-slate-300 hover:text-rose-500 transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="bg-slate-50/80 p-4 border-t border-slate-100 flex items-center justify-between text-[11px] font-black text-slate-500 uppercase tracking-tight">
                <div>예상 총 증거금: <span className="text-slate-800 ml-1">{(form.custom_targets?.reduce((acc: number, t: any) => acc + (t.target_qty || 0), 0) || 0).toLocaleString()} USDT</span></div>
                <div>총 진입 가치: <span className="text-slate-800 ml-1">{(form.custom_targets?.reduce((acc: number, t: any) => acc + (t.target_qty || 0), 0) * (form.leverage || 1)).toLocaleString()} USDT</span></div>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground text-center font-medium">※ 직접 입력 모드에서는 간격(%)보다 설정된 진입가와 수량이 우선 적용됩니다.</p>
          </div>
        )}
      </div>
    </div>
  );
};
